import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { EXERCISE_DEEP_LINK_PARAM, exerciseById } from '@erectus/shared';
import type { ProgramSnack } from '@erectus/shared';
import { Button } from '@/ui/primitives.js';
import { Icon } from '@/ui/icons.js';
import { useOrigin } from '@/ui/Layout.js';
import { ExerciseIllustration } from '@/features/exercises/ExerciseIllustration.js';

/** One exercise snack: its moves, each linking to the full instructions, and Done. */
export function SnackCard({
  snack,
  done,
  onDone,
}: {
  snack: ProgramSnack;
  done: boolean;
  onDone?: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const origin = useOrigin();
  return (
    <div className="space-y-2 rounded-2xl border border-line bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold">{snack.title}</p>
        <span className="text-xs text-muted">about {snack.minutes} min</span>
      </div>
      <ol className="space-y-1.5">
        {snack.exerciseIds.map((id) => {
          const exercise = exerciseById(id);
          if (!exercise) return null;
          return (
            <li key={id}>
              <Link
                to={`/exercises?${EXERCISE_DEEP_LINK_PARAM}=${id}`}
                state={origin}
                className="flex items-center gap-3 rounded-xl p-1 hover:bg-surface"
              >
                <ExerciseIllustration id={id} fallback={exercise.illustration} />
                <span className="flex-1">
                  <span className="block text-sm font-medium">{exercise.name}</span>
                  <span className="block text-xs text-muted">{exercise.reps}</span>
                </span>
                <Icon name="back" size={14} className="rotate-180 text-muted" />
              </Link>
            </li>
          );
        })}
      </ol>
      {onDone ? (
        <Button
          variant={done ? 'secondary' : 'primary'}
          disabled={done || busy}
          onClick={() => {
            setBusy(true);
            onDone();
          }}
        >
          {done ? (
            <span className="flex items-center justify-center gap-1">
              <Icon name="check" size={16} strokeWidth={2.4} /> Done today
            </span>
          ) : (
            'Mark snack as done'
          )}
        </Button>
      ) : null}
    </div>
  );
}
